import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Wallet, Settings2, Trash2, Pencil, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RevenueTemplateFormDialog from "@/pages/daily_revenues/RevenueTemplateFormDialog";
import DailyRevenueFormDialog from "@/pages/daily_revenues/DailyRevenueFormDialog";
import {
  FIELD_LABELS,
  REVENUE_FIELDS,
  STATUS_LABELS,
  StatusBadge,
  formatDay,
  formatFt,
} from "@/pages/daily_revenues/helpers";

const STATUS_FILTERS = [
  { v: "", label: "Mind" },
  { v: "new", label: "Új" },
  { v: "verifying", label: "Ellenőrzés alatt" },
  { v: "shortage", label: "Hiány" },
  { v: "shortage_resolved", label: "Hiány rendezve" },
  { v: "approved", label: "Lezárva" },
];

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

export default function DailyRevenuesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("entries");
  const [entries, setEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [openEntry, setOpenEntry] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadEntries = async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const { data } = await api.get("/daily-revenues", { params });
    setEntries(data || []);
  };
  const loadTemplates = async () => {
    const { data } = await api.get("/revenue-templates");
    setTemplates(data || []);
  };
  const reload = async () => {
    setLoading(true);
    try {
      await Promise.all([loadEntries(), loadTemplates()]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [statusFilter]);

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Törlöd a "${t.site_name}" sablont?`)) return;
    try {
      await api.delete(`/revenue-templates/${t._id}`);
      toast.success("Sablon törölve.");
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="daily-revenues-page">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <Wallet className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
            Pénzügy
          </span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">
          Napi bevételek
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Telephely-szintű sablonokra alapozott napi bevétel rögzítés, címlet-szintű
          ellenőrzés és hiány-kezelés.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="entries" data-testid="tab-entries">
            Bejegyzések ({entries.length})
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            Sablonok ({templates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="entries" className="mt-4">
          <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-1.5">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.v || "all"}
                    type="button"
                    onClick={() => setStatusFilter(f.v)}
                    data-testid={`rev-filter-${f.v || "all"}`}
                    className={`h-8 rounded-full px-3 text-xs font-medium transition-colors ${
                      statusFilter === f.v
                        ? "bg-foreground text-background"
                        : "bg-muted/40 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                onClick={() => setOpenEntry(true)}
                disabled={templates.filter((t) => t.active !== false).length === 0}
                title={templates.filter((t) => t.active !== false).length === 0 ? "Először hozz létre egy sablont." : undefined}
                data-testid="new-entry-button"
              >
                <Plus className="h-4 w-4" /> Új bejegyzés
              </Button>
            </header>

            {loading ? (
              <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
              </div>
            ) : entries.length === 0 ? (
              <EmptyEntries onCreate={() => setOpenEntry(true)} hasTpl={templates.length > 0} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                      <Th>Dátum</Th>
                      <Th>Telephely</Th>
                      <Th className="hidden text-right md:table-cell">Készpénz</Th>
                      <Th className="hidden text-right md:table-cell">Kártya</Th>
                      <Th className="hidden text-right lg:table-cell">Egyéb</Th>
                      <Th className="text-right">Összes</Th>
                      <Th className="hidden sm:table-cell">Státusz</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const other = REVENUE_FIELDS
                        .filter((f) => f !== "cash" && f !== "card")
                        .reduce((s, f) => s + (Number(e.amounts?.[f]) || 0), 0);
                      return (
                        <tr
                          key={e._id}
                          data-testid={`rev-row-${e._id}`}
                          onClick={() => navigate(`/daily-revenues/${e._id}`)}
                          className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                        >
                          <Td className="font-medium">{formatDay(e.date)}</Td>
                          <Td>
                            <div className="font-medium">{e.site_name || "—"}</div>
                            {e.company_name && (
                              <div className="text-[11px] text-muted-foreground">
                                {e.company_name}
                              </div>
                            )}
                          </Td>
                          <Td className="hidden text-right tabular-nums md:table-cell">
                            {formatFt(e.amounts?.cash)}
                          </Td>
                          <Td className="hidden text-right tabular-nums md:table-cell">
                            {formatFt(e.amounts?.card)}
                          </Td>
                          <Td className="hidden text-right tabular-nums text-muted-foreground lg:table-cell">
                            {other ? formatFt(other) : "—"}
                          </Td>
                          <Td className="text-right tabular-nums font-semibold">
                            {formatFt(e.total)}
                          </Td>
                          <Td className="hidden sm:table-cell"><StatusBadge status={e.status} /></Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            <header className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <p className="text-sm font-medium">Bevétel sablonok</p>
                <p className="text-[11px] text-muted-foreground">
                  Egy sablon / telephely. A switch-ek határozzák meg, mit rögzítenek.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => { setEditingTemplate(null); setOpenTemplate(true); }}
                data-testid="new-template-button"
              >
                <Plus className="h-4 w-4" /> Új sablon
              </Button>
            </header>

            {loading ? (
              <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">Nincs sablon</p>
                <p className="text-xs text-muted-foreground">
                  Hozz létre egy sablont egy telephelyre, hogy elindítsd a napi
                  bevételek rögzítését.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {templates.map((t) => (
                  <li
                    key={t._id}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                    data-testid={`tpl-row-${t._id}`}
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium">
                        {t.site_name}
                        {t.active === false && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                            inaktív
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {t.company_name || "—"}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {REVENUE_FIELDS.filter((f) => t.fields?.[f]).map((f) => (
                          <span
                            key={f}
                            className="inline-flex items-center rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-medium"
                          >
                            {FIELD_LABELS[f]}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {t.public_slug && (
                        <CopyLinkButton slug={t.public_slug} testId={`tpl-share-${t._id}`} />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingTemplate(t); setOpenTemplate(true); }}
                        data-testid={`tpl-edit-${t._id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Szerkesztés
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteTemplate(t)}
                        className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                        data-testid={`tpl-delete-${t._id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </TabsContent>
      </Tabs>

      <DailyRevenueFormDialog
        open={openEntry}
        onOpenChange={setOpenEntry}
        onSaved={(saved) => {
          setOpenEntry(false);
          if (saved?._id) navigate(`/daily-revenues/${saved._id}`);
        }}
      />
      <RevenueTemplateFormDialog
        open={openTemplate}
        onOpenChange={(v) => { if (!v) setEditingTemplate(null); setOpenTemplate(v); }}
        initial={editingTemplate}
        onSaved={() => { setOpenTemplate(false); setEditingTemplate(null); loadTemplates(); }}
      />
    </div>
  );
}

function EmptyEntries({ onCreate, hasTpl }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        <Wallet className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium">
        {hasTpl ? "Még nincs bejegyzés" : "Nincs sablon — kezdd ott!"}
      </p>
      <p className="text-xs text-muted-foreground">
        {hasTpl
          ? "Rögzíts egy napi bevételt a kiválasztott sablon szerint."
          : "Hozz létre egy sablont a 'Sablonok' fülön, utána tudsz bejegyzést rögzíteni."}
      </p>
      {hasTpl && (
        <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" /> Új bejegyzés
        </Button>
      )}
    </div>
  );
}

function CopyLinkButton({ slug, testId }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/dr/${slug}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link a vágólapon", { description: url });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Másold ki a linket:", url);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={copy}
      className="h-8 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
      data-testid={testId}
      title="Publikus rögzítő-link másolása"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
      {copied ? "Másolva" : "Link másolása"}
    </Button>
  );
}


export { STATUS_LABELS };
